import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import { useFormat } from '@/lib/format.js';
import { buttonClass } from '@/lib/styles.js';

/**
 * The "Filters" button beside a list's search box (MYP-128).
 *
 * The extra filters — goods type on Packages, channel on Orders — fold away behind it, so the list
 * starts higher on the screen. It says how many filters are on, so a narrowed list is never a
 * mystery even with the panel closed. On a phone it is the icon alone (still named for a screen
 * reader), leaving the width to the search box.
 */
export function FiltersToggle({
  open,
  activeCount,
  controls,
  onToggle,
  buttonRef,
}: {
  open: boolean;
  activeCount: number;
  /** The panel's id, for `aria-controls`. */
  controls: string;
  onToggle: () => void;
  buttonRef?: RefObject<HTMLButtonElement | null>;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
      className={buttonClass(
        'secondary',
        cn('shrink-0 gap-1.5 px-3', (open || activeCount > 0) && 'border-brand text-brand'),
      )}
    >
      <Icon name="tune" size={18} />
      <span className="sr-only sm:not-sr-only">{t('listFilters.button')}</span>
      {activeCount > 0 ? (
        <span className="flex size-5 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white tabular-nums">
          {fmt.number(activeCount)}
        </span>
      ) : null}
    </button>
  );
}

/**
 * The fold-out panel the Filters button opens (MYP-128). Inline rather than floating: it pushes
 * the list down while open, never covers it, and needs no positioning on a phone.
 *
 * Esc or Done closes it and hands focus back to the Filters button, so a keyboard user is not left
 * on a control that has just disappeared.
 */
export function FiltersPanel({
  id,
  open,
  onClose,
  returnFocusTo,
  children,
}: {
  id: string;
  open: boolean;
  onClose: () => void;
  returnFocusTo: RefObject<HTMLButtonElement | null>;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(open);

  useEffect(() => {
    // Opening moves focus into the panel's first control; closing hands it back to the button.
    if (open && !wasOpen.current)
      panelRef.current?.querySelector<HTMLElement>('select, input, button')?.focus();
    if (!open && wasOpen.current && panelRef.current === null) returnFocusTo.current?.focus();
    wasOpen.current = open;
  }, [open, returnFocusTo]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      id={id}
      role="group"
      aria-label={t('listFilters.panelLabel')}
      onKeyDown={(event) => {
        // An open dropdown in the panel handles its own Escape (MYP-137): that one closes the
        // list, not the panel.
        if (event.key === 'Escape' && !event.defaultPrevented) {
          event.stopPropagation();
          onClose();
        }
      }}
      className="flex flex-wrap items-end gap-x-5 gap-y-3 rounded-lg bg-surface p-4 shadow-card"
    >
      {children}
      <button type="button" onClick={onClose} className={buttonClass('ghost', 'ml-auto px-3')}>
        {t('listFilters.done')}
      </button>
    </div>
  );
}

/**
 * One active filter, shown while the panel is closed — one tap removes it (MYP-128). An on/off
 * filter ("Not on an order yet") names itself, so it has no `label`.
 */
export function ActiveFilterChip({
  label,
  value,
  onRemove,
}: {
  label?: string;
  value: string;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft py-0.5 pr-0.5 pl-3 text-sm text-ink">
      {label === undefined ? null : <span className="text-muted">{label}:</span>}
      <span className="font-medium">{value}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={
          label === undefined
            ? t('listFilters.removeOnly', { value })
            : t('listFilters.remove', { label, value })
        }
        className="flex size-11 items-center justify-center rounded-full text-muted transition-colors hover:bg-white hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:size-7"
      >
        <Icon name="close" size={16} />
      </button>
    </span>
  );
}
